import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import RoleSelectFilter from "../components/common/RoleSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CreateUserModal from "../components/users/CreateUserModal";
import UpdateUserModal from "../components/users/UpdateUserModal";
import ViewUserModal from "../components/users/ViewUserModal";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getUsers,
  getUserById,
  deleteUser,
} from "../redux/actions/userActions";
import { getRoles } from "../redux/actions/roleActions";
import { userActions } from "../redux/slices/user";

import { USER_STATUS_OPTIONS } from "../shared/data/enumOptions";
import { UserSummaryDto } from "../shared/dtos/user/UserSummaryDto";
import { castEnumForDisplay } from "../helpers/enumHelpers";
import { UserStatus } from "../shared/enums/UserStatus";

const UsersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: users,
    loadingFetchAll: loadingUsers,
    errorFetchAll: errorLoadingUsers,
    pagination,
    serverStatus,
    loadingCreate: loadingUserCreate,
    loadingUpdate: loadingUserUpdate,
    loadingDelete: loadingUserDelete,
    item: user,
    loadingFetchOne: loadingUser,
    errorFetchOne: errorLoadingUser,
    errorCreate: errorCreatingUser,
    errorUpdate: errorUpdatingUser,
  } = useAppSelector((state) => state.user);

  const { items: roles } = useAppSelector((state) => state.role);
  const { userInfo } = useAppSelector((state) => state.auth);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [userForOperation, setUserForOperation] =
    useState<UserSummaryDto | null>(null);

  const canCreateUser = hasPermission("user:create");
  const canEditUser = hasPermission("user:update");
  const canDeleteUser = hasPermission("user:delete");
  const canViewUser = hasPermission("user:read");

  useEffect(() => {
    if (!roles.length) dispatch(getRoles());
  }, [dispatch, roles.length]);

  const fetchUsers = useCallback(() => {
    dispatch(
      getUsers({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        roleId: selectedRoleId || undefined,
        status: (selectedStatus as UserStatus) || undefined,
      }),
    );
  }, [
    debouncedSearch,
    dispatch,
    selectedBranchId,
    selectedRoleId,
    selectedStatus,
    pageConfig.limit,
    pageConfig.page,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingUserCreate) setIsCreateModalOpen(false);
  }, [loadingUserCreate]);

  const handleOpenUpdate = (selectedUser: UserSummaryDto): void => {
    setUserForOperation(selectedUser);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback(() => {
    if (!loadingUserUpdate) {
      setIsUpdateModalOpen(false);
      setUserForOperation(null);
    }
  }, [loadingUserUpdate]);

  const handleOpenDelete = (selectedUser: UserSummaryDto): void => {
    setUserForOperation(selectedUser);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback(() => {
    if (!loadingUserDelete) {
      setIsDeleteModalOpen(false);
      setUserForOperation(null);
    }
  }, [loadingUserDelete]);

  const handleOpenView = (selectedUser: UserSummaryDto): void => {
    dispatch(getUserById(selectedUser.id));
    setIsViewModalOpen(true);
  };

  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (userForOperation) {
      dispatch(deleteUser(userForOperation.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;
    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(userActions.setServerStatus(null));
  }, [
    dispatch,
    handleCloseCreate,
    handleCloseDelete,
    handleCloseUpdate,
    serverStatus,
  ]);

  const columns: TableColumn<UserSummaryDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Full Name",
        minWidth: 170,
        format: (_, row) => (
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {row.firstName} {row.middleName ? `${row.middleName} ` : ""}
            {row.lastName}
          </Typography>
        ),
      },
      { id: "email", label: "Email Address", minWidth: 200 },
      {
        id: "userRole",
        label: "Role",
        minWidth: 120,
        format: (_, row) => castEnumForDisplay(row.userRole) || row.userRole,
      },
      {
        id: "userStatus",
        label: "Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.userStatus} />,
      },
      {
        id: "createdAt",
        label: "Joined",
        minWidth: 120,
        format: (_, row) => new Date(row.createdAt).toLocaleDateString(),
      },
    ],
    [],
  );

  const renderRowActions = (row: UserSummaryDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: canViewUser,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Edit User",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canEditUser,
      },
      {
        label: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDeleteUser,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="User Management"
      subtitle="Manage system access, roles, and administrative accounts."
      actions={
        canCreateUser ? (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreate}
          >
            Create New User
          </AppButton>
        ) : undefined
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(event) => {
          setSearchTerm(event.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search users..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <RoleSelectFilter value={selectedRoleId} onChange={setSelectedRoleId} />
        <AppSelectFilter
          label="Status"
          value={selectedStatus}
          onChange={setSelectedStatus}
          options={USER_STATUS_OPTIONS}
          allLabel="All Statuses"
        />
      </PageToolbar>

      <StandardTable<UserSummaryDto>
        columns={columns}
        data={users}
        loading={loadingUsers}
        error={errorLoadingUsers}
        onRetry={fetchUsers}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {isCreateModalOpen && userInfo && (
        <CreateUserModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingUserCreate}
          error={errorCreatingUser}
        />
      )}

      {isUpdateModalOpen && userForOperation && userInfo && (
        <UpdateUserModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          user={userForOperation}
          loading={loadingUserUpdate}
          error={errorUpdatingUser}
        />
      )}

      {isViewModalOpen && (
        <ViewUserModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          user={user}
          loading={loadingUser}
          error={errorLoadingUser}
        />
      )}

      {isDeleteModalOpen && userForOperation && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete User"
          description={`Are you sure you want to delete the user "${userForOperation.firstName} ${userForOperation.lastName}"? This action cannot be undone.`}
          loading={loadingUserDelete}
        />
      )}
    </PageContainer>
  );
};

export default UsersPage;
